import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { FotoProduto } from '../../components/FotoProduto';
import { formatMoeda, calcularValorFaltante, temConversaoDeUnidade } from '@fiodecobre/shared';

interface NotaItem {
  id: number;
  numero_item: number | null;
  produto_id: number | null;
  codigo_interno?: string | null;
  produto_descricao?: string | null;
  descricao: string | null;
  unidade: string | null;
  quantidade: string;
  qtd_real: string | null;
  valor_unitario: string;
  foto_url?: string | null;
  // [DA-559] Item que ja voltou pro fornecedor — nao pode virar "nao chegou" (sairia do estoque
  // duas vezes). O backend recusa; aqui a linha desabilita e DIZ por que.
  qtd_devolvida?: string | null;
  devolucao_nfe_numero?: number | null;
}

interface Props {
  notaId: number;
  numeroNota: string;
  itens: NotaItem[];
  onFechar: () => void;
  onGravado: () => void;
}

interface LinhaState {
  qtd_recebida: number;
  motivo: string;
}

export function ModalDivergenciaRecebimento({ notaId, numeroNota, itens, onFechar, onGravado }: Props) {
  const toast = useToast();
  const [salvando, setSalvando] = useState(false);
  const [gerarCpCredito, setGerarCpCredito] = useState(true);
  const [linhas, setLinhas] = useState<Record<number, LinhaState>>({});

  useEffect(() => {
    const inicial: Record<number, LinhaState> = {};
    for (const it of itens) {
      const qtdNota = Number(it.qtd_real ?? it.quantidade);
      inicial[it.id] = { qtd_recebida: qtdNota, motivo: '' };
    }
    setLinhas(inicial);
  }, [itens]);

  const calculos = useMemo(() => {
    let totalFaltante = 0;
    const detalhes: Array<{ id: number; qtdNota: number; qtdFaltante: number; valorFaltante: number; motivo: string }> = [];
    for (const it of itens) {
      const linha = linhas[it.id];
      if (!linha) continue;
      // [DA-559] Item ja devolvido nao entra na conta nem no payload — o backend recusaria e a
      // gravacao inteira falharia por causa de uma linha que a tela nem devia ter oferecido.
      if (Number(it.qtd_devolvida || 0) > 0) continue;
      const qtdNota = Number(it.qtd_real ?? it.quantidade);
      const qtdFaltante = Math.max(0, qtdNota - linha.qtd_recebida);
      const valorFaltante = qtdFaltante * Number(it.valor_unitario);
      totalFaltante += valorFaltante;
      detalhes.push({ id: it.id, qtdNota, qtdFaltante, valorFaltante, motivo: linha.motivo });
    }
    return { totalFaltante, detalhes };
  }, [itens, linhas]);

  function setQtdRecebida(itemId: number, valor: number) {
    setLinhas((prev) => {
      const atual = prev[itemId];
      if (!atual) return prev;
      return { ...prev, [itemId]: { ...atual, qtd_recebida: Math.max(0, valor) } };
    });
  }

  function setMotivo(itemId: number, motivo: string) {
    setLinhas((prev) => {
      const atual = prev[itemId];
      if (!atual) return prev;
      return { ...prev, [itemId]: { ...atual, motivo } };
    });
  }

  async function gravar() {
    const itensPayload = calculos.detalhes
      .filter((d) => d.qtdFaltante > 0)
      .map((d) => ({ item_id: d.id, qtd_faltante: d.qtdFaltante, motivo: d.motivo || null }));

    if (itensPayload.length === 0) {
      toast.erro('Marque pelo menos 1 item com qtd recebida menor que a qtd da nota');
      return;
    }

    setSalvando(true);
    try {
      await api.post(`/api/notas-entrada/${notaId}/divergencias`, {
        itens: itensPayload,
        gerar_cp_credito: gerarCpCredito,
      });
      toast.sucesso(
        gerarCpCredito
          ? `Divergencia registrada. CP de credito gerado (${formatMoeda(calculos.totalFaltante)})`
          : `Divergencia registrada (${formatMoeda(calculos.totalFaltante)})`,
      );
      onGravado();
      onFechar();
    } catch (err: any) {
      toast.erro(err.message || 'Erro ao registrar divergencia');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto={true}
      onFechar={onFechar}
      titulo={`Registrar divergencia de recebimento — NF ${numeroNota}`}
      largura={1100}
      rodape={
        <div className="flex items-center justify-between w-full">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={gerarCpCredito}
              onChange={(e) => setGerarCpCredito(e.target.checked)}
            />
            Gerar credito automatico no CP do fornecedor
          </label>
          <div className="flex items-center gap-4">
            <div className="text-sm">
              Total faltante: <strong className="text-red-700">{formatMoeda(calculos.totalFaltante)}</strong>
            </div>
            <button
              type="button"
              onClick={onFechar}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={gravar}
              disabled={salvando || calculos.totalFaltante <= 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {salvando ? 'Registrando...' : 'Registrar Divergencia'}
            </button>
          </div>
        </div>
      }
    >
      <div className="text-xs muted mb-2">
        Ajuste a coluna <strong>Qtd Recebida</strong> dos itens que nao chegaram (parcial ou total).
        A nota fica fiel ao XML; apenas o estoque sera reduzido pelo que faltou
        {gerarCpCredito && ', e um CP negativo sera lancado contra o fornecedor'}.
      </div>
      <table className="w-full text-sm">
        <thead className="grid-subheader">
          <tr>
            <th className="text-center w-10">#</th>
            <th className="text-center w-10">Foto</th>
            <th className="text-left w-20">Cod.</th>
            <th className="text-left">Produto / Descricao NF</th>
            <th className="text-right w-20">Qtd Nota</th>
            <th className="text-right w-24">Qtd Recebida</th>
            <th className="text-right w-20">Faltante</th>
            <th className="text-right w-28">Valor Faltante</th>
            <th className="text-left w-48">Motivo (opcional)</th>
          </tr>
        </thead>
        <tbody className="grid-body">
          {itens.map((it) => {
            const linha = linhas[it.id];
            const qtdNota = Number(it.qtd_real ?? it.quantidade);
            const qtdFaltante = linha ? Math.max(0, qtdNota - linha.qtd_recebida) : 0;
            // Mesma peca do backend ([[DA-559]]) — a previa nao pode divergir do que grava.
            const valorFaltante = calcularValorFaltante({
              qtdFaltante,
              quantidadeNota: Number(it.quantidade),
              qtdReal: it.qtd_real === null ? null : Number(it.qtd_real),
              valorUnitario: Number(it.valor_unitario),
            });
            const converte = temConversaoDeUnidade(Number(it.quantidade), it.qtd_real === null ? null : Number(it.qtd_real));
            const devolvido = Number(it.qtd_devolvida || 0) > 0;
            const destaque = qtdFaltante > 0 && !devolvido;
            return (
              <tr key={it.id} className="border-b" style={destaque ? { backgroundColor: '#fef3c7' } : undefined}>
                <td className="px-2 py-2 text-center text-xs muted">{it.numero_item ?? '—'}</td>
                <td className="px-1 py-2 text-center">
                  {it.foto_url ? (
                    <FotoProduto url={it.foto_url} tam={28} style={{ margin: '0 auto' }} />
                  ) : (
                    <span className="opacity-40 text-xs">—</span>
                  )}
                </td>
                <td className="px-2 py-2 codigo-curto text-xs">{it.codigo_interno || '—'}</td>
                <td className="px-2 py-2 text-xs">
                  <div className="font-medium text-green-700">{it.produto_descricao || '—'}</div>
                  <div className="text-xs muted truncate max-w-[280px]">{it.descricao || ''}</div>
                  {devolvido && (
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', lineHeight: 1.3, marginTop: 2 }}>
                      Ja devolvido ao fornecedor pela NF-e {String(it.devolucao_nfe_numero ?? '').padStart(6, '0')} —
                      a mercadoria ja saiu do estoque por ali
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-right text-xs">
                  {qtdNota.toLocaleString('pt-BR')}
                  {/* Sem isto o operador le "1.200" ao lado de uma nota que diz "1 CD" e nao sabe
                      qual dos dois o campo espera — foi o que tornou o erro de unidade possivel. */}
                  {converte && (
                    <div className="text-[10px] muted leading-tight">
                      a nota traz {Number(it.quantidade).toLocaleString('pt-BR')} {it.unidade || 'emb.'}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    max={qtdNota}
                    value={linha?.qtd_recebida ?? qtdNota}
                    disabled={devolvido}
                    onChange={(e) => setQtdRecebida(it.id, Number(e.target.value))}
                    className="w-20 text-right text-xs border rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    style={{ borderColor: 'var(--input-border)' }}
                  />
                </td>
                <td className="px-2 py-2 text-right text-xs font-medium" style={{ color: destaque ? '#b91c1c' : 'inherit' }}>
                  {qtdFaltante > 0 ? qtdFaltante.toLocaleString('pt-BR') : '—'}
                </td>
                <td className="px-2 py-2 text-right text-xs font-medium" style={{ color: destaque ? '#b91c1c' : 'inherit' }}>
                  {valorFaltante > 0 ? formatMoeda(valorFaltante) : '—'}
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    value={linha?.motivo ?? ''}
                    onChange={(e) => setMotivo(it.id, e.target.value)}
                    disabled={qtdFaltante <= 0}
                    placeholder={qtdFaltante > 0 ? 'Item nao chegou' : ''}
                    className="w-full text-xs border rounded px-2 py-1 disabled:bg-gray-50"
                    style={{ borderColor: 'var(--input-border)' }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Modal>
  );
}
